/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ファネル詳細ページで共有する定数（ファネル名 / チャート色 / 診断コア質問 / 分析タイプラベル / ページソースラベル / buy_type ラベル / フォームフィールドラベル）。
 */

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ファネルチャートの系列カラーパレットを定義する
 * @graph-connects none
 */
export const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
];

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ファネルIDの日英表示名を定義する
 * @graph-connects none
 */
export const FUNNEL_NAMES: Record<string, { ja: string; en: string }> = {
  registration: { ja: '新規登録', en: 'Registration' },
  purchase: { ja: 'アイテム購入', en: 'Purchase' },
  return: { ja: '返却', en: 'Return' },
  designation: { ja: 'スタイリスト指名', en: 'Designation' },
  diagnosis: { ja: 'パーソナルカラー診断', en: 'Diagnosis' },
  option_change: { ja: 'オプション変更', en: 'Option Change' },
  plan_change: { ja: 'プラン変更', en: 'Plan Change' },
};

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 診断ファネルのコア設問IDの集合を定義する
 * @graph-connects none
 */
export const DIAGNOSIS_CORE_QUESTIONS = new Set([
  'Q1',
  'Q2',
  'Q3',
  'Q6',
  'Q7',
  'Q8',
  'Q9',
  'Q10',
  'Q13',
  'Q14',
  'Q15',
  'Q16',
  'Q17',
  'Q18',
  'Q19',
  'Q20',
]);

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ファネル深掘り分析タイプの日英表示ラベルを定義する
 * @graph-connects none
 */
export const ANALYSIS_TYPE_LABELS: Record<string, { ja: string; en: string }> = {
  basic_info_fields: { ja: '基本情報フィールド入力率', en: 'Basic Info Field Completion' },
  source_funnel: { ja: 'ページ別ファネル', en: 'Page Source Funnel' },
  buy_type_breakdown: { ja: '購入タイプ別内訳', en: 'Buy Type Breakdown' },
  qa_answers: { ja: 'QA回答パターン', en: 'QA Answer Patterns' },
  plan_selection: { ja: 'プラン選択分布', en: 'Plan Selection' },
  coupon_usage: { ja: 'クーポン利用', en: 'Coupon Usage' },
  payment_method: { ja: '支払い方法', en: 'Payment Method' },
  scroll_depth: { ja: 'スクロール深度', en: 'Scroll Depth' },
  repeat_method: { ja: 'リピート方法率', en: 'Repeat Method Rate' },
  sagawa_availability: { ja: '佐川利用可能率', en: 'Sagawa Availability' },
  method_trend: { ja: '返却方法トレンド', en: 'Method Trend' },
  first_vs_change: { ja: '初回vs変更', en: 'First vs Change' },
  stylist_count: { ja: 'スタイリスト数', en: 'Stylist Count' },
  premium: { ja: 'プレミアム率', en: 'Premium Rate' },
  color_answers: { ja: 'カラー回答', en: 'Color Answers' },
  result_distribution: { ja: '結果分布', en: 'Result Distribution' },
  guest_rate: { ja: 'ゲスト率', en: 'Guest Rate' },
  share_behavior: { ja: 'シェア行動', en: 'Share Behavior' },
  current_options: { ja: '現在のオプション', en: 'Current Options' },
  eligibility: { ja: '利用資格', en: 'Eligibility' },
  block_reasons: { ja: 'ブロック理由', en: 'Block Reasons' },
  disabled_all: { ja: '全制限ユーザー', en: 'All Disabled' },
  np_user: { ja: 'NPユーザー', en: 'NP User' },
  settlement_day: { ja: '決済日影響', en: 'Settlement Day' },
  plan_direction: { ja: 'プラン変更方向', en: 'Plan Direction' },
  course_months: { ja: 'コース月数', en: 'Course Months' },
  selectable_plans: { ja: '選択可能プラン', en: 'Selectable Plans' },
  lock_status: { ja: 'ロック状態', en: 'Lock Status' },
  pending_changes: { ja: '保留中の変更', en: 'Pending Changes' },
  option_holders: { ja: 'オプション保有者', en: 'Option Holders' },
};

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ページソース別の日本語表示ラベルを定義する
 * @graph-connects none
 */
export const PAGE_SOURCE_LABELS: Record<string, string> = {
  home: 'ホーム',
  items_checkout: 'アイテム詳細（Native）',
  deliveries_rental_items: '配送中アイテム',
  past_items: '過去アイテム',
  favorites: 'お気に入り',
  wearing_history: '着用履歴',
  coordinate: 'コーディネート',
  album: 'アルバム',
  ec: 'EC',
  '(unknown)': '不明',
  other: 'その他',
};

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 購入タイプコードの日本語表示ラベルを定義する
 * @graph-connects none
 */
export const BUY_TYPE_LABELS: Record<string, string> = {
  '1': 'PAST_ITEM（過去レンタル品）',
  '2': 'DIFF_COLOR（色違い）',
  '3': 'NOW_ITEM（レンタル中）',
  '4': 'RECOMMEND_EC（EC推薦）',
  '5': 'PAST_ITEM_DIFF_COLOR（過去品色違い）',
  '6': 'EC_ITEM（EC商品）',
};

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 登録フォーム項目の日本語表示ラベルを定義する
 * @graph-connects none
 */
export const FIELD_LABELS: Record<string, string> = {
  'name.lastName': '姓',
  'name.firstName': '名',
  'name.kanaLastName': '姓（カナ）',
  'name.kanaFirstName': '名（カナ）',
  phoneNumber: '電話番号',
  'address.postalCode': '郵便番号',
  'address.street': '住所',
  'address.building': '建物名',
};

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 登録フォームの必須項目の集合を定義する
 * @graph-connects none
 */
export const REQUIRED_FIELDS = new Set([
  'name.lastName',
  'name.firstName',
  'phoneNumber',
  'address.postalCode',
  'address.street',
]);
